import React, { ReactNode } from 'react';
import { NotificationMessageList, NotificationMessageSingle } from './Notification';
import { AlertType } from '../../graphql/schema/p4types';
import { makeStyles, Toast, ToastBody, ToastTitle } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { CheckmarkCircle16Filled, ErrorCircle16Filled, Info16Filled, Warning16Filled } from '@fluentui/react-icons';

const useToastIconStyles = makeStyles({
  success: {
    color: p4Tokens.colorStatusSuccessForeground1,
  },
  info: {
    color: p4Tokens.colorNeutralForeground2,
  },
  warning: {
    color: p4Tokens.colorStatusWarningForeground1,
  },
  danger: {
    color: p4Tokens.colorStatusDangerForeground1,
  },
});

export const useToastIcon = (
  type?: AlertType | null
): {
  icon: typeof CheckmarkCircle16Filled;
  className: string;
} => {
  const toastIconStyles = useToastIconStyles();

  switch (type) {
    case AlertType.Success:
      return { icon: CheckmarkCircle16Filled, className: toastIconStyles.success };
    case AlertType.Info:
      return { icon: Info16Filled, className: toastIconStyles.info };
    case AlertType.Warning:
      return { icon: Warning16Filled, className: toastIconStyles.warning };
    case AlertType.Error:
    default:
      return { icon: ErrorCircle16Filled, className: toastIconStyles.danger };
  }
};

/**
 * Similar to Notification, but design adjusted for display in the "action centre" panel.
 */
export const DismissedNotification = ({
  type,
  title,
  message,
}: {
  type?: AlertType;
  title: string;
  message: string;
}) => {
  const messages: ReactNode[] = [];
  message?.split('\n').forEach((msg, index) => {
    // We must use the index because the same message 'may' appear twice in these lists, despite ideally not doing this.
    //  The messages should always be parsed in a consistent order though so that should be fine.
    // eslint-disable-next-line react/no-array-index-key
    messages.push(<li key={`${msg}_${index}`}>{msg}</li>);
  });

  //The Toast normally gets an intent (for the icon) from internal fluent-ui context from the toaster,
  //we cannot use that for dismissed toasts so manually add the icon.
  const { icon: ToastIcon, className: iconClassname } = useToastIcon(type);

  return (
    <div
      data-testid={`notification-${type}-dismissed`}
      style={{
        paddingBottom: '7px',
        paddingTop: '3px',
        width: '100%',
      }}
    >
      <Toast data-testid={`notification-${type}`}>
        <ToastTitle data-testid="notification-title" media={{ children: <ToastIcon className={iconClassname} /> }}>
          {title ?? 'Status'}
        </ToastTitle>
        <ToastBody>
          {messages.length > 1 ? (
            <NotificationMessageList>{messages}</NotificationMessageList>
          ) : (
            <NotificationMessageSingle>{message}</NotificationMessageSingle>
          )}
        </ToastBody>
      </Toast>
    </div>
  );
};
